import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import blogPosts from '../../data/blogPosts';

const Blog = () => {
    const [activeTag, setActiveTag] = useState('All');

    const tags = useMemo(
        () => ['All', ...Array.from(new Set(blogPosts.map((p) => p.tag)))],
        [],
    );

    const posts = useMemo(
        () => (activeTag === 'All' ? blogPosts : blogPosts.filter((p) => p.tag === activeTag)),
        [activeTag],
    );

    return (
        <div>
            {/* ===== Masthead ===== */}
            <section className="relative bg-white dark:bg-gray-900 overflow-hidden">
                <div className="blueprint absolute inset-0" />
                <div className="absolute inset-0 bg-gradient-to-b from-white dark:from-gray-900 via-transparent to-white dark:to-gray-900" />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-10 text-center">
                    <h1 className="font-serif text-4xl sm:text-6xl font-medium text-gray-900 dark:text-white">
                        Stories, guides &amp; inspiration
                    </h1>
                    <p className="text-gray-500 dark:text-gray-400 mt-4 max-w-xl mx-auto">
                        Everything we know about finding, licensing and working with free visuals —
                        written for the people who actually use them.
                    </p>
                </div>
            </section>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
                {/* ===== Tag filter ===== */}
                <div className="flex flex-wrap items-center justify-center gap-2 pt-2 pb-12">
                    {tags.map((tag) => {
                        const active = tag === activeTag;
                        return (
                            <button
                                key={tag}
                                type="button"
                                onClick={() => setActiveTag(tag)}
                                className={`inline-flex items-center min-h-[44px] px-4 rounded-full text-sm font-semibold transition-all duration-300 ${
                                    active
                                        ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900 shadow-e1'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                                }`}
                            >
                                {tag}
                            </button>
                        );
                    })}
                </div>

                {/* ===== Alternating editorial rows ===== */}
                <div className="space-y-16 sm:space-y-24">
                    {posts.map((post, i) => {
                        const flip = i % 2 === 1;
                        return (
                            <Link
                                key={post.slug}
                                to={`/blog/${post.slug}`}
                                className="group grid md:grid-cols-2 gap-8 lg:gap-14 items-center"
                            >
                                {/* image */}
                                <div
                                    className={`relative overflow-hidden rounded-3xl shadow-e2 ring-1 ring-black/5 aspect-[4/3] ${
                                        flip ? 'md:order-2' : ''
                                    }`}
                                >
                                    <img
                                        src={post.cover}
                                        alt={post.title}
                                        loading="lazy"
                                        className="absolute inset-0 w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-[900ms]"
                                    />
                                    <span className="absolute top-4 left-4 inline-flex rounded-full bg-white/90 dark:bg-gray-900/90 backdrop-blur px-3 py-1 text-xs font-bold uppercase tracking-wide text-brand-600 dark:text-brand-400 shadow">
                                        {post.tag}
                                    </span>
                                </div>

                                {/* copy */}
                                <div className={flip ? 'md:order-1' : ''}>
                                    <span className="font-serif text-6xl sm:text-7xl font-medium text-gray-100 dark:text-gray-800 leading-none tabular-nums select-none block">
                                        0{i + 1}
                                    </span>
                                    <h2 className="font-serif text-2xl sm:text-4xl font-medium text-gray-900 dark:text-white leading-tight mt-2 group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors">
                                        {post.title}
                                    </h2>
                                    <p className="text-gray-500 dark:text-gray-400 mt-4 max-w-md leading-relaxed">
                                        {post.excerpt}
                                    </p>
                                    <div className="flex items-center gap-3 mt-6 text-sm text-gray-400">
                                        <span className="font-semibold text-gray-700 dark:text-gray-300">{post.author}</span>
                                        <span className="h-1 w-1 rounded-full bg-gray-400" />
                                        <span>{post.date}</span>
                                        <span className="h-1 w-1 rounded-full bg-gray-400" />
                                        <span>{post.readTime}</span>
                                    </div>
                                    <span className="mt-6 inline-flex items-center gap-2 text-brand-500 font-bold">
                                        Read article
                                        <FontAwesomeIcon icon={faArrowRight} className="transform group-hover:translate-x-1 transition-transform" />
                                    </span>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default Blog;
